import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  IconButton,
  useTheme,
  Stack,
  Divider,
} from '@mui/material';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import GitHubIcon from '@mui/icons-material/GitHub';
import EmailIcon from '@mui/icons-material/Email';
import TwitterIcon from '@mui/icons-material/Twitter';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const Footer = () => {
  const theme = useTheme();
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Portfolio', path: '/portfolio' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <Box
      component="footer"
      sx={{
        background: 'linear-gradient(135deg, #374151 0%, #1f2937 100%)',
        color: 'white',
        pt: 6,
        pb: 3,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4} sx={{ mb: 4 }}>
          {/* Company Info */}
          <Grid item xs={12} md={4}>
            <Stack spacing={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  component="img"
                  src={`${process.env.PUBLIC_URL}images/logo.png`}
                  alt="DevHopes Labs"
                  sx={{
                    height: 35,
                    width: 'auto',
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: 'white',
                    fontSize: '1.3rem',
                  }}
                >
                  DevHopes Labs
                </Typography>
              </Box>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: 'grey.300', 
                  lineHeight: 1.6,
                  maxWidth: 320,
                }}
              >
                Transforming ideas into exceptional digital experiences.
              </Typography>
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                  Follow Us
                </Typography>
                <Stack direction="row" spacing={1.5}>
                  <IconButton
                    href="https://www.linkedin.com/company/devhopeslabs"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      bgcolor: 'rgba(20, 184, 166, 0.1)',
                      color: '#14b8a6',
                      p: 1.5,
                      '&:hover': {
                        bgcolor: '#14b8a6',
                        color: 'white',
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <LinkedInIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Box>
            </Stack>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Quick Links
            </Typography>
            <Stack spacing={1.5}>
              {quickLinks.map((link) => (
                <Link
                  key={link.name}
                  component={RouterLink}
                  to={link.path}
                  sx={{
                    color: 'grey.300',
                    textDecoration: 'none',
                    fontSize: '0.95rem',
                    display: 'block',
                    py: 0.5,
                    '&:hover': {
                      color: '#14b8a6',
                      transform: 'translateX(4px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  {link.name}
                </Link>
              ))}
            </Stack>
          </Grid>


          {/* Contact Info */}
          <Grid item xs={12} md={3}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Contact Info
            </Typography>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <EmailIcon sx={{ color: '#14b8a6', fontSize: 20 }} />
                <Link
                  href="mailto:info@devhopeslabs.com"
                  sx={{
                    color: 'grey.300',
                    textDecoration: 'none',
                    fontSize: '0.95rem',
                    '&:hover': { color: '#14b8a6' },
                  }}
                >
                  info@devhopeslabs.nl
                </Link>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <LocationOnIcon sx={{ color: '#14b8a6', fontSize: 20 }} />
                <Typography sx={{ color: 'grey.300', fontSize: '0.95rem' }}>
                  Bornholmstraat 27, 1339SN, Almere
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <PhoneIcon sx={{ color: '#14b8a6', fontSize: 20 }} />
                <a href="tel:+31616811749" style={{ textDecoration: 'none' }}>
                  <Typography sx={{ color: 'grey.300', fontSize: '0.95rem' }}>
                    Call us
                  </Typography>
                </a>
              </Box>
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            py: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: 'grey.400', textAlign: { xs: 'center', sm: 'left' } }}>
            © {currentYear} DevHopes Labs. All rights reserved.
          </Typography>
              
          <Typography variant="body2" sx={{ color: 'grey.400', textAlign: { xs: 'center', sm: 'left' } }}>
            KVK: 97643076 VAT: NL005278834B03
          </Typography>
          <Stack direction="row" spacing={3}>
            <Link
              component={RouterLink}
              to="/privacy-policy"
              sx={{
                color: 'grey.400',
                textDecoration: 'none',
                fontSize: '0.875rem',
                '&:hover': { color: '#14b8a6' },
              }}
            >
              Privacy Policy
            </Link>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer; 
